import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import React from 'react';

/**
 * Generic search controls component for list pages
 * Provides search input, sorting, date filter (optional), and advanced search with filters
 *
 * @param {Object} props
 * @param {string} props.searchValue - Current search value
 * @param {Function} props.setSearchValue - Search value setter
 * @param {string} [props.searchFieldLabel='Search'] - Label for search field (used when no searchTypeOptions)
 * @param {Array} [props.searchTypeOptions] - Search type options [{value, label}] for the prefix dropdown
 * @param {string} [props.searchType] - Current selected search type
 * @param {Function} [props.setSearchType] - Search type setter
 * @param {Array} props.sortByList - Sort options [{value, label}]
 * @param {string} props.sortBy - Current sort field
 * @param {Function} props.setSortBy - Sort field setter
 * @param {string} props.sortOrder - Current sort order
 * @param {Function} props.setSortOrder - Sort order setter
 * @param {boolean} [props.enableDateFilter=false] - Show preset date filter dropdown
 * @param {string} props.dateFilter - Current date filter: 'today' | 'yesterday' |
 *   'tomorrow' | 'this_week' | 'this_month' | 'this_year' | 'last_week' |
 *   'last_month' | 'last_year' | 'last_seven_days' | 'last_thirty_days' |
 *   'last_sixty_days' | 'last_ninety_days' | 'last_one_eighty_days' | 'default'
 * @param {Function} props.setDateFilter - Date filter setter
 * @param {boolean} [props.enableDateRangePicker=false] - Show custom date range inputs (From / To)
 * @param {string} props.dateRangeStart - Custom range start date (ISO string)
 * @param {Function} props.setDateRangeStart - Start date setter
 * @param {string} props.dateRangeEnd - Custom range end date (ISO string)
 * @param {Function} props.setDateRangeEnd - End date setter
 * @param {Array} [props.quickFilters=[]] - Extra dropdowns shown after the date
 *   filter: [{id, label, value, onChange, options: [{value, label}]}], where
 *   value 'default' means unset
 * @param {boolean} props.showAdvancedSearch - Advanced search visibility
 * @param {Function} props.setShowAdvancedSearch - Toggle advanced search
 * @param {Array} props.filters - Active filters
 * @param {Function} props.setFilters - Filters setter
 * @param {Object} props.filterDraft - Current draft filter {field, value}
 * @param {Function} props.setFilterDraft - Draft filter setter
 * @param {Array} props.filterOptions - Available filter fields [{value, label}]
 * @param {Function} props.addFilter - Add filter handler
 * @param {Function} props.removeFilter - Remove filter handler
 * @param {Function} props.formatFieldLabel - Format filter field label
 * @param {Array} [props.conditionalFieldInputs=[]] - Config for conditional inputs
 * @param {Function} props.handleSearchClick - Search button handler
 * @param {Function} props.resetAll - Reset button handler
 * @param {string} [props.searchHelperText] - Helper text shown below the search field
 * @param {boolean} [props.hideSearchBar=false] - Hide the search input and action buttons
 *   (use when the parent provides its own search UI, e.g. PackageSearchPanel)
 */
export default function GenericSearchControls({
  searchValue,
  setSearchValue,
  searchFieldLabel = 'Search',
  searchTypeOptions,
  searchType,
  setSearchType,
  sortByList,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  enableDateFilter = false,
  dateFilter,
  setDateFilter,
  enableDateRangePicker = false,
  dateRangeStart = '',
  setDateRangeStart,
  dateRangeEnd = '',
  setDateRangeEnd,
  showAdvancedSearch,
  setShowAdvancedSearch,
  filters,
  setFilters,
  filterDraft,
  setFilterDraft,
  filterOptions,
  addFilter,
  removeFilter,
  formatFieldLabel,
  conditionalFieldInputs = [],
  quickFilters = [],
  handleSearchClick,
  resetAll,
  hideSearchBar = false,
}) {
  // Find if current filterDraft.field has conditional input config
  const currentFieldConfig = conditionalFieldInputs.find(
    (config) => config.field === filterDraft.field
  );

  const hasAnyValue =
    !!searchValue ||
    filters.length > 0 ||
    sortBy !== 'default' ||
    sortOrder !== 'default' ||
    (enableDateFilter && dateFilter !== 'default') ||
    (enableDateRangePicker && !!dateRangeStart) ||
    (enableDateRangePicker && !!dateRangeEnd) ||
    quickFilters.some((quickFilter) => quickFilter.value !== 'default');

  const activeSearchLabel = searchTypeOptions
    ? (searchTypeOptions.find((o) => o.value === searchType)?.label ?? searchFieldLabel)
    : searchFieldLabel;

  const searchPlaceholder = searchTypeOptions
    ? `Search by ${activeSearchLabel.toLowerCase()}…`
    : `Search…`;

  return (
    <>
      <Box mb={2} display="flex" alignItems="center" flexWrap="wrap">
        {/* Search type prefix dropdown (optional) */}
        {!hideSearchBar && searchTypeOptions && (
          <Select
            value={searchType}
            onChange={(e) => {
              setSearchType(e.target.value);
              setSearchValue('');
            }}
            size="small"
            sx={{
              minWidth: 160,
              height: 56,
              marginRight: 0,
              marginBottom: 1,
              borderRadius: '4px 0 0 4px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderRight: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderRight: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderRight: 'none',
              },
              backgroundColor: 'grey.50',
            }}
          >
            {searchTypeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        )}

        {/* Search TextField */}
        {!hideSearchBar && (
          <TextField
            label={searchTypeOptions ? undefined : activeSearchLabel}
            placeholder={searchPlaceholder}
            variant="outlined"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              marginRight: 1,
              marginBottom: 1,
              minWidth: 260,
              ...(searchTypeOptions && {
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0 4px 4px 0',
                },
              }),
            }}
          />
        )}

        {/* Sort By Select */}
        <FormControl
          style={{ minWidth: 160, marginRight: 8, marginBottom: 8 }}
          variant="outlined"
        >
          {sortBy !== 'default' && (
            <InputLabel id="sort-by-label">Sort By</InputLabel>
          )}
          <Select
            labelId={sortBy !== 'default' ? 'sort-by-label' : undefined}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            displayEmpty
            label={sortBy !== 'default' ? 'Sort By' : undefined}
          >
            <MenuItem value="default">
              <em>Sort By</em>
            </MenuItem>
            {sortByList.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sort Order Select */}
        <FormControl
          style={{ minWidth: 160, marginRight: 8, marginBottom: 8 }}
          variant="outlined"
        >
          {sortOrder !== 'default' && (
            <InputLabel id="sort-order-label">Order</InputLabel>
          )}
          <Select
            labelId={sortOrder !== 'default' ? 'sort-order-label' : undefined}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            displayEmpty
            label={sortOrder !== 'default' ? 'Order' : undefined}
          >
            <MenuItem value="default">
              <em>Order</em>
            </MenuItem>
            <MenuItem value="asc">Ascending</MenuItem>
            <MenuItem value="desc">Descending</MenuItem>
          </Select>
        </FormControl>

        {/* Conditional Date Filter */}
        {enableDateFilter && (
          <FormControl
            style={{ minWidth: 160, marginRight: 8, marginBottom: 8 }}
            variant="outlined"
          >
            {dateFilter !== 'default' && (
              <InputLabel id="date-filter-label">Date Filter</InputLabel>
            )}
            <Select
              labelId={dateFilter !== 'default' ? 'date-filter-label' : undefined}
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              displayEmpty
              label={dateFilter !== 'default' ? 'Date Filter' : undefined}
            >
              <MenuItem value="default">
                <em>Date Filter ↑↓</em>
              </MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="tomorrow">Tomorrow</MenuItem>
              <MenuItem value="this_week">This Week</MenuItem>
              <MenuItem value="this_month">This Month</MenuItem>
              <MenuItem value="this_year">This Year</MenuItem>
              <MenuItem value="last_week">Last week</MenuItem>
              <MenuItem value="last_month">Last month</MenuItem>
              <MenuItem value="last_year">Last year</MenuItem>
              <MenuItem value="last_seven_days">Last 7 days</MenuItem>
              <MenuItem value="last_thirty_days">Last 30 days</MenuItem>
              <MenuItem value="last_sixty_days">Last 60 days</MenuItem>
              <MenuItem value="last_ninety_days">Last 90 days</MenuItem>
              <MenuItem value="last_one_eighty_days">Last 180 days</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Quick Filter Selects */}
        {quickFilters.map((quickFilter) => {
          const labelId = `${quickFilter.id}-label`;
          const hasValue = quickFilter.value !== 'default';
          return (
            <FormControl
              key={quickFilter.id}
              style={{ minWidth: 160, marginRight: 8, marginBottom: 8 }}
              variant="outlined"
            >
              {hasValue && (
                <InputLabel id={labelId}>{quickFilter.label}</InputLabel>
              )}
              <Select
                labelId={hasValue ? labelId : undefined}
                value={quickFilter.value}
                onChange={(e) => quickFilter.onChange(e.target.value)}
                displayEmpty
                label={hasValue ? quickFilter.label : undefined}
              >
                <MenuItem value="default">
                  <em>{quickFilter.label}</em>
                </MenuItem>
                {quickFilter.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        })}

        {/* Custom Date Range Picker */}
        {enableDateRangePicker && (
          <>
            <TextField
              label="Date From"
              type="date"
              value={dateRangeStart}
              onChange={(e) => setDateRangeStart && setDateRangeStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              style={{ marginRight: 8, marginBottom: 8 }}
            />
            <TextField
              label="Date To"
              type="date"
              value={dateRangeEnd}
              onChange={(e) => setDateRangeEnd && setDateRangeEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              style={{ marginRight: 8, marginBottom: 8 }}
            />
          </>
        )}

        {/* Advanced Search Toggle */}
        <Button
          variant="text"
          onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
          style={{
            marginBottom: 8,
            height: 56,
            padding: '6px 16px'
          }}
        >
          {showAdvancedSearch ? 'Hide Advanced Search' : 'Advanced Search'}
        </Button>

        {/* Search Button */}
        {!hideSearchBar && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearchClick}
            disabled={!hasAnyValue}
            style={{
              marginLeft: 8,
              marginBottom: 8,
              height: 56,
              padding: '6px 16px'
            }}
          >
            Search
          </Button>
        )}

        {/* Reset Button */}
        {!hideSearchBar && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={resetAll}
            style={{
              marginRight: 8,
              marginBottom: 8,
              height: 56,
              padding: '6px 16px'
            }}
          >
            Reset
          </Button>
        )}
      </Box>

      {/* Advanced Search Section */}
      <Collapse in={showAdvancedSearch}>
        <Box mb={2}>
          <Box display="flex" alignItems="center" flexWrap="wrap" mb={2}>
            {/* Field Selector */}
            <FormControl
              style={{ minWidth: 200, marginRight: 8, marginBottom: 8 }}
              variant="outlined"
            >
              {filterDraft.field && (
                <InputLabel id="filter-field-label">Select Field</InputLabel>
              )}
              <Select
                labelId={filterDraft.field ? 'filter-field-label' : undefined}
                value={filterDraft.field}
                onChange={(e) =>
                  setFilterDraft((prev) => ({ ...prev, field: e.target.value }))
                }
                displayEmpty
                label={filterDraft.field ? 'Select Field' : undefined}
              >
                <MenuItem value="" disabled>
                  <em>Select Field</em>
                </MenuItem>
                {filterOptions.map(({ value, label }) => {
                  const alreadyUsed = filters.some((f) => f.field === value);
                  return (
                    <MenuItem key={value} value={value} disabled={alreadyUsed}>
                      {alreadyUsed ? (
                        <CheckBoxIcon
                          fontSize="small"
                          style={{ marginRight: 8 }}
                        />
                      ) : (
                        <CheckBoxOutlineBlankIcon
                          fontSize="small"
                          style={{ marginRight: 8 }}
                        />
                      )}
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {/* Conditional Value Input - Select or TextField */}
            {currentFieldConfig ? (
              <FormControl
                style={{ minWidth: 200, marginRight: 8, marginBottom: 8 }}
                variant="outlined"
              >
                {filterDraft.value && (
                  <InputLabel id="filter-value-label">
                    {currentFieldConfig.label || 'Value'}
                  </InputLabel>
                )}
                <Select
                  labelId={filterDraft.value ? 'filter-value-label' : undefined}
                  value={filterDraft.value}
                  onChange={(e) =>
                    setFilterDraft((prev) => ({ ...prev, value: e.target.value }))
                  }
                  displayEmpty
                  label={filterDraft.value ? (currentFieldConfig.label || 'Value') : undefined}
                >
                  <MenuItem value="" disabled>
                    <em>Select {currentFieldConfig.label || 'Value'}</em>
                  </MenuItem>
                  {currentFieldConfig.options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Value"
                variant="outlined"
                value={filterDraft.value}
                onChange={(e) =>
                  setFilterDraft((prev) => ({ ...prev, value: e.target.value }))
                }
                style={{ marginRight: 8, marginBottom: 8 }}
              />
            )}

            {/* Add Filter Button */}
            <Button
              variant="outlined"
              onClick={addFilter}
              style={{
                marginBottom: 8,
                height: 56,
                padding: '6px 16px'
              }}
            >
              + Add Filter
            </Button>
          </Box>

          {/* Active Filters */}
          {filters.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {filters.map((filter) => (
                <Chip
                  key={filter.field}
                  label={`${formatFieldLabel(filter.field)}: ${filter.value}`}
                  onDelete={() => removeFilter(filter.field)}
                  color="primary"
                />
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </>
  );
}
